import { v2 } from '../v2/tokens.js';
// The violet tint is the violet family's wash and line, so the two cannot drift.
const lightViolet = {
    base: '#7c3aed',
    deep: '#5b21b6',
    wash: '#f1eafe',
    line: '#cdb6f7',
};
const lightInk = '#1b1530';
// The HTML's `--green-deep`: the green button's lip and a held verdict's words.
const lightGreenDeep = '#0b7a4f';
const darkViolet = {
    base: '#8250f5',
    deep: '#5328c8',
    wash: '#2c2058',
    line: '#6447b8',
};
/*
 * Light is the design HTML's, value for value:
 * ~/vault/platform/2026-10-28-marketing-mix-decide-style/marketing-mix-mobile.html.
 * The HTML has no dark scheme, so dark is drawn here for a dark canvas rather than
 * derived from light; `tokens.test.ts` refuses a dark role that is a dimmed copy.
 */
export const v3 = {
    color: {
        light: {
            canvas: '#fbfaff',
            stage: '#ece6fa',
            ink: lightInk,
            // A tinted card's line: `.concept p`, between ink and muted.
            inkSoft: '#3b3552',
            // `.body`: a screen's running text.
            body: '#2c2643',
            muted: '#5d5873',
            line: '#e6e1f2',
            violet: lightViolet,
            green: '#16a36b',
            // `--green-wash`: an option row the verdict held.
            greenWash: '#dcf7ea',
            gold: '#f59e0b',
            surface: '#ffffff',
            // `.term.saved`: a saved term's card.
            surfaceSaved: '#fcfaff',
            // `.score strong`: a result tile's number.
            greenInk: '#0b7a4f',
            onAccent: '#ffffff',
            // The drawers' buttons: the screenshots draw them, the stylesheet does not.
            action: {
                green: { fill: '#16a36b', lip: lightGreenDeep },
                amber: { fill: '#d97706', lip: '#92400e' },
            },
            control: {
                icon: '#6a6482',
                iconOff: '#c4bfd6',
                track: '#e7e2f3',
                badge: '#8a84a2',
                off: { fill: '#ece8f6', ink: '#a59fbb', lip: '#ddd7ec' },
            },
            tint: {
                violet: {
                    fill: lightViolet.wash,
                    edge: lightViolet.line,
                    ink: lightViolet.deep,
                },
                amber: { fill: '#fff3d6', edge: '#f5cf6b', ink: '#8a4b06' },
                teal: { fill: '#dcf6ef', edge: '#8edcc8', ink: '#0f6f5e' },
                pink: { fill: '#fde6f6', edge: '#f0a8dd', ink: '#9d1b83' },
            },
            // `.morning-visual`: the sky a time window is drawn on.
            sky: { fill: '#deeff8', label: '#24506a', ink: '#03649a' },
            // `.shade`, `.extra` and `.status`: the veil is ink, laid at `opacity.veil`.
            sheet: {
                surface: '#ffffff',
                handle: '#d9d4e6',
                veil: lightInk,
                extra: '#f4f2f9',
                good: lightGreenDeep,
            },
        },
        dark: {
            canvas: '#140d26',
            stage: '#241b44',
            ink: '#f4f1ff',
            inkSoft: '#ddd6f5',
            body: '#e6e0fa',
            muted: '#b6add6',
            line: '#382d5c',
            violet: darkViolet,
            green: '#2fd58f',
            greenWash: '#0f3b2c',
            gold: '#ffb938',
            surface: '#1f1740',
            surfaceSaved: '#251c4a',
            greenInk: '#4fe0a6',
            onAccent: '#fbf8ff',
            action: {
                green: { fill: '#13a06b', lip: '#0d6b47' },
                amber: { fill: '#c4620c', lip: '#7a3508' },
            },
            control: {
                icon: '#a69dc8',
                iconOff: '#4a4170',
                track: '#2f2652',
                badge: '#9b92bf',
                off: { fill: '#2a2348', ink: '#6e6592', lip: '#352c5a' },
            },
            tint: {
                violet: {
                    fill: darkViolet.wash,
                    edge: darkViolet.line,
                    ink: '#d6c6ff',
                },
                amber: { fill: '#3a2709', edge: '#a8721a', ink: '#ffd27d' },
                teal: { fill: '#0b302c', edge: '#1f8a77', ink: '#7fe8cf' },
                pink: { fill: '#3b1238', edge: '#a33d92', ink: '#ffaee9' },
            },
            sky: { fill: '#0f2a3b', label: '#a9d2e8', ink: '#7cc9f2' },
            sheet: {
                surface: '#1d1a3e',
                handle: '#4d4470',
                veil: '#05020e',
                extra: '#2a2550',
                good: '#5ee0a8',
            },
        },
    },
    type: {
        // The HTML's rounded stack. No app loads one yet, so a phone falls back to the
        // system face, as v2's Archivo does.
        family: { ui: 'Nunito' },
        // React Native takes weights in hundreds, so the HTML's 850 is 800 here.
        scale: {
            display: { size: 30, line: 34, weight: 800 },
            title: { size: 25, line: 29, weight: 800 },
            heading: { size: 17, line: 21, weight: 800 },
            action: { size: 17, line: 22, weight: 800 },
            body: { size: 16.5, line: 23, weight: 400 },
            caption: { size: 12.5, line: 18, weight: 400 },
            eyebrow: { size: 11.5, line: 17, weight: 800 },
            // The primitives' own steps, each the HTML's size for that control.
            quiet: { size: 15, line: 20, weight: 800 },
            option: { size: 16, line: 21, weight: 600 },
            // `.compact`: the design's three choice screens, sized to fit their options.
            optionCompact: { size: 15, line: 20, weight: 600 },
            titleCompact: { size: 23, line: 27, weight: 800 },
            bodyCompact: { size: 15, line: 21, weight: 400 },
            // `.art-credit` under a still, and `.lens-intro strong` over a quote.
            side: { size: 13.5, line: 17, weight: 700 },
            sideRole: { size: 12, line: 16, weight: 400 },
            speaker: { size: 16, line: 21, weight: 700 },
            coach: { size: 15, line: 21, weight: 400 },
            card: { size: 18, line: 23, weight: 800 },
            cardLine: { size: 14, line: 18, weight: 400 },
            hint: { size: 13, line: 18, weight: 800 },
            chip: { size: 12.5, line: 18, weight: 800 },
            badge: { size: 12, line: 16, weight: 800 },
            barTitle: { size: 12, line: 16, weight: 800 },
            sheetTitle: { size: 24, line: 29, weight: 800 },
            status: { size: 16, line: 21, weight: 900 },
            extra: { size: 15.5, line: 22, weight: 400 },
            // The screens' own steps.
            lead: { size: 16, line: 23, weight: 400 },
            question: { size: 23, line: 27, weight: 800 },
            hero: { size: 27, line: 31, weight: 800 },
            termBody: { size: 15.5, line: 22, weight: 400 },
            score: { size: 28, line: 40, weight: 900 },
            // `.time-labels strong`: an hour a time window opens or closes on.
            time: { size: 20, line: 24, weight: 900 },
            scoreLabel: { size: 12.5, line: 18, weight: 700 },
            fact: { size: 22, line: 28, weight: 800 },
            captionStrong: { size: 14, line: 19, weight: 700 },
            target: { size: 16, line: 20, weight: 800 },
            lensRow: { size: 15, line: 20, weight: 800 },
            row: { size: 16, line: 20, weight: 800 },
            rowCaption: { size: 11.5, line: 16, weight: 700 },
            placed: { size: 11.5, line: 16, weight: 400 },
            factContext: { size: 11, line: 16, weight: 800 },
        },
        tracking: { action: 0.6, eyebrow: 0.7, caps: 0.6, barTitle: 0.4 },
    },
    size: {
        action: 56,
        quiet: 50,
        option: 56,
        optionCompact: 52,
        hint: 38,
        badge: 28,
        tap: 44,
        tapNarrow: 40,
        bar: 54,
        dot: 10,
        mascot: 44,
        icon: 22,
        glyph: 20,
        glyphSmall: 16,
        stroke: 1.6,
        // The HTML's selected-option tick is a heavy text glyph, not a line icon.
        strokeBold: 3,
        card: 150,
        handle: { width: 40, height: 5 },
        status: 40,
        symbol: 46,
        // How far a drag must carry the sheet down before letting go closes it.
        swipe: 80,
        // `.term-mark`: the tinted square a term's object sits in.
        mark: 48,
        // `.fact-dot`, `.sort-target` and `.take-row`: classify's counter and targets.
        factDot: 8,
        factIcon: 30,
        target: 78,
        row: 68,
        // The under-the-fold fade: v2's, since both draw the same cue.
        fade: v2.space.idea,
        // A score share's edge. Square, because a chat crops anything else to one.
        scoreCard: 360,
    },
    motion: { reveal: v2.motion.duration.reveal },
    // `.sort-target:disabled`: the one unavailable choice the HTML draws.
    // `streak`: the one pass of light across a button turning green.
    opacity: { off: 0.55, veil: 0.4, placed: 0.8, streak: 0.35 },
    // The phone a screen story is framed at: v2's, since both designs draw one phone.
    // `narrow` is the iPhone SE's width, the smallest a lesson's data must fit.
    device: { width: v2.device.width, height: v2.device.height, narrow: 375 },
    // Vertical, then horizontal padding, as the HTML writes them.
    inset: {
        action: { y: 12, x: 15 },
        quiet: { y: 10, x: 10 },
        option: { y: 10, x: 13 },
        optionCompact: { y: 8, x: 12 },
        card: { y: 12, x: 13 },
        chip: { y: 5, x: 10 },
        hint: { y: 5, x: 12 },
        bubble: { y: 11, x: 14 },
        bar: { y: 0, x: 12 },
        dots: { y: 0, x: 6 },
        sheet: { top: 8, x: 20, bottom: 14 },
        extra: { y: 13, x: 14 },
        term: { y: 14, x: 15 },
        category: { y: 8, x: 4 },
        hero: { y: 18, x: 18 },
        footer: { y: 10, x: 18 },
        fact: { y: 20, x: 18 },
        target: { y: 8, x: 10 },
        row: { y: 8, x: 12 },
    },
    gap: {
        dot: 4,
        hint: 5,
        action: 8,
        coach: 10,
        grid: 10,
        fact: 5,
        option: 11,
        // `.option-list`.
        options: 9,
        extra: 14,
        // `main > * + *`: between a screen's blocks.
        section: 14,
        sectionCompact: 11,
    },
    space: {
        xs: v2.space.xs,
        sm: v2.space.sm,
        md: v2.space.md,
        lg: v2.space.lg,
        xl: v2.space.xl,
        page: 18,
    },
    radius: {
        badge: 9,
        chip: 10,
        tap: 12,
        dot: 6,
        tail: 6,
        hint: 14,
        category: 16,
        quiet: 17,
        control: 18,
        card: 20,
        art: 22,
        hero: 26,
        sheet: 30,
        handle: 8,
        symbol: 13,
        extra: 15,
        fact: 5,
        pill: v2.radius.pill,
    },
    border: {
        line: v2.border.edge,
        lip: 5,
        lipSmall: v2.border.lip,
    },
};
// Each tint's object colours, `{ primary, secondary }` as a course pair takes them: the
// design draws the four Ps' objects in their card's hue. One pair serves both themes.
export const v3ObjectPalette = {
    violet: { primary: '#6d28d9', secondary: '#8b5cf6' },
    amber: { primary: '#d97706', secondary: '#fbbf24' },
    teal: { primary: '#0f8a74', secondary: '#2dd4bf' },
    pink: { primary: '#c026d3', secondary: '#e879f9' },
};
//# sourceMappingURL=tokens.js.map